import React from "react";
import { useContent } from "./content";
import { useHomeLang } from "./homeLang";
import { Bullets, Donut, LineChart, LowerThird, Panel, Screen, Ticker, TitleText, You } from "./StudioElements";

// The product's content accent, which the brand colour does not change.
const GOLD = "#e0a33e";

// The words in the frame, in each language: the allocation's asset classes,
// the week's days under the chart, and how the frame reads to a screen reader.
const WORDS = {
  it: {
    assets: ["Azionario", "Obbligazionario", "Liquidità"],
    days: ["Lun", "Mar", "Mer", "Gio", "Ven"],
    preview: ({ title, name, disclaimer }) => `Anteprima del video: ${title}, con ${name}${disclaimer ? " e le avvertenze sempre visibili" : ""}`,
  },
  en: {
    assets: ["Equities", "Bonds", "Cash"],
    days: ["Mon", "Tue", "Wed", "Thu", "Fri"],
    preview: ({ title, name, disclaimer }) => `Video preview: ${title}, with ${name}${disclaimer ? " and the disclaimer always on screen" : ""}`,
  },
};

const ALLOCATION = [{ value: 45, color: GOLD }, { value: 40, color: "#4682b4" }, { value: 15, color: "#2fa36b" }];

function Allocation({ title }) {
  const { assets } = WORDS[useHomeLang()];
  return (
    <Panel title={title}>
      <Donut shares={ALLOCATION.map((share, i) => ({ ...share, label: assets[i] }))} />
    </Panel>
  );
}

function Team({ title }) {
  const { DEMO_TEAM } = useContent();
  return (
    <Panel title={title}>
      <Bullets items={DEMO_TEAM.map(({ name, role }) => `${name}, ${role}`)} />
    </Panel>
  );
}

function Markets({ title }) {
  const { days } = WORDS[useHomeLang()];
  return (
    <>
      <TitleText>{title}</TitleText>
      <Screen>
        <LineChart series="FTSE MIB" labels={days} values={[100, 101.2, 100.6, 102.1, 102.9]} />
      </Screen>
    </>
  );
}

const Speaker = ({ title }) => <TitleText>{title}</TitleText>;

// Each studio template is one of the product's layouts: the speaker alone
// ("Relatore"), a screen beside them ("Immersive"), or the content filling
// the right half ("Affiancato"). The prompt is what the teleprompter shows
// the advisor while they record, in each language. Keyed by the need's id (content.js).
export const SCENES = {
  update: {
    layout: "side",
    Content: Allocation,
    prompt: {
      it: ["Buongiorno, ecco com’è andato il mese", "per il vostro portafoglio.", "La parte azionaria ha tenuto bene,", "le obbligazioni ci hanno dato stabilità.", "Vi racconto cosa teniamo d’occhio."],
      en: ["Good morning, here’s how the month went", "for your portfolio.", "Equities held up well,", "and bonds kept things steady.", "Here’s what we’re keeping an eye on."],
    },
  },
  intro: {
    layout: "speaker",
    Content: Speaker,
    prompt: {
      it: ["Piacere, sono il vostro consulente.", "Vi racconto come lavoro e per chi:", "seguo famiglie e imprenditori", "da più di dieci anni,", "sempre con lo stesso metodo."],
      en: ["Nice to meet you, I’m your advisor.", "Let me tell you how I work, and for whom:", "I’ve worked with families and business owners", "for more than ten years,", "always with the same approach."],
    },
  },
  family: {
    layout: "side",
    Content: Team,
    prompt: {
      it: ["Vi presento le persone", "che seguono il vostro patrimonio.", "Caterina analizza i mercati ogni giorno,", "Diego risponde alle vostre richieste,", "Laura vi segue sugli aspetti fiscali."],
      en: ["Let me introduce the people", "who look after your wealth.", "Caterina follows the markets every day,", "Diego handles your requests,", "Laura takes care of your tax matters."],
    },
  },
  markets: {
    layout: "immersive",
    Content: Markets,
    prompt: {
      it: ["Buongiorno a tutti, oggi parliamo", "di cosa cambia per voi.", "La Borsa di Milano chiude la settimana", "in rialzo di quasi il tre per cento.", "Vediamo insieme da dove viene."],
      en: ["Good morning, everyone. Today we’re looking at", "what’s changing for you.", "The Milan stock exchange is closing the week", "up almost three percent.", "Let’s look at where that’s coming from."],
    },
  },
};

// One frame of the finished video, as the studio composes it. Until the
// camera is on, a stand-in waits on a plain backdrop; then the visitor's
// feed takes the place of both, their own room and all, as the product
// films anyone without a green screen.
export default function StudioScene({ scene, title, name, role, brand, disclaimer, camera }) {
  const { layout, Content } = scene;
  const { preview } = WORDS[useHomeLang()];
  return (
    <figure className={`st-pv st-pv-${layout}`} aria-label={preview({ title, name, disclaimer })}>
      <div className="st-stage" aria-hidden="true">
        <span className="st-cam">
          {camera.status === "on" ? <video ref={camera.video} className="st-video" autoPlay playsInline muted /> : <You />}
        </span>
        <Content title={title} />
        <LowerThird name={name} title={role} brand={brand} />
        {disclaimer && <Ticker text={disclaimer} />}
      </div>
    </figure>
  );
}
